<template>
  <div ref="wrapperEl" class="player-wrapper" :aria-busy="hidden">
    <span class="d-sr-only" role="status" aria-live="polite">{{ hidden ? t(l.film.videoLoading) : '' }}</span>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div
      class="videoContainer-inner"
      :class="{ active: hidden === false }"
      :aria-hidden="hidden ? 'true' : undefined"
      :inert="hidden || undefined"
      v-html="player"
    ></div>
    <OverlayRating
      v-if="isTeacherOrSuperUser && video"
      :show="showRating"
      :content-id="video.id"
      :rating="rating"
      @rate="onRate"
      @close="showRating = false"
    />
    <div v-if="isVideoEnded && !showRating" class="watch-again-overlay">
      <v-btn prepend-icon="fa:fas fa-arrow-rotate-left" color="white" size="large" @click="watchAgain">
        {{ t(l.quiz.replay_film) }}
      </v-btn>
    </div>
  </div>
</template>
<script setup lang="ts">
import VideoPlayer from '@ilteducation/video-player/src/player';
import '@ilteducation/video-player/src/scss/style.scss';
import { OverlayRating } from '@ilteducation/customer-ratings/overlay-rating';
import { stopAllSounds } from '@ilteducation/audio-resources';
import { useLocale, useLocalisation } from '@/plugins/translation';
import { useTokens, useUserContext } from '@ilteducation/ilt-auth';
import { computed, nextTick, onMounted, onUnmounted, ref, shallowRef, watch, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import useLegacyLanguageMap from '@/composables/use-legacy-language-map';
import useSnackbar from '@/composables/use-snackbar';
import useLanguageTranslations from '@/composables/use-language-translations';
import type { Video } from '@ilteducation/videos';
import type { SlimOrganization } from '@ilteducation/my-account-types';
import { useMyOrganizations } from '@ilteducation/my-account';
import useGuiStore from '@/store/gui';
import useAudioStore from '@/store/audio';
import { storeToRefs } from 'pinia';
import type { Area, Category } from '@ilteducation/content-ref-data-types';
import { useVideos } from '@ilteducation/videos';
import { useConfigHelper } from '@ilteducation/config';
import useLanguagesStore from '@/store/languages';
import useAppVersion from '@/composables/use-app-version';
import { sendVideoSlicePlayedAnalytics } from '@/composables/use-send-video-analytics';
import { videoSessionId } from '@/composables/use-video-session';

const route = useRoute();
const { language: currentLanguageIso } = useLocalisation();
const { isoCodeToShortCode } = useLegacyLanguageMap();
const { setNarrationPlaying } = useAudioStore();
const { getDownloadUrl } = useVideos();
const { isNarrationAudioPlaying } = storeToRefs(useAudioStore());
const { t, l } = useLocale();
const { hasRole, isSuperUser } = useUserContext();

const props = defineProps<{
  video: Video;
  category: Category;
  area?: Area;
  rating?: 1 | 0 | null;
}>();
type EmitRate = {
  contentId: string | number;
  value: 1 | 0 | null;
};

const emit = defineEmits<{
  (e: 'rate', payload: EmitRate): void;
}>();

const onRate = (payload: EmitRate) => {
  emit('rate', payload);
};

const isTeacherOrSuperUser = hasRole('employee') || isSuperUser;
const currentLanguage = computed(() => isoCodeToShortCode(currentLanguageIso.value));
const wrapperEl = ref<HTMLElement | null>(null);
const hasAutofocusedPlay = ref(false);
const player = ref<string | null>(null);
const hidden = ref(true);
const signedUrl = ref('');
const isVideoEnded = ref(false);
const playerInstance = shallowRef<VideoPlayer | null>(null);
const showRating = ref(false);
const snackbar = useSnackbar();
const { populateLanguage, languageTranslated } = useLanguageTranslations();
const guiStore = useGuiStore();
const { settings } = storeToRefs(guiStore);
// Warning: ILTVideo cannot be a ref(). If it is made a ref(), the reactive properties from Vue will be propagated
// deeply into the ILTVideo player making all objects into proxies. This somehow destroys the subtitle tracks
// in videojs.
let ILTVideo: VideoPlayer | null = null;

const { getMarketLanguage } = useLanguagesStore();
const { getApiUrl } = useConfigHelper();
const userContext = useUserContext();
const { organizations } = useMyOrganizations(true);
const { appVersion } = useAppVersion();

const startTimeFromSubtitle = async (): Promise<string> => {
  const selectedLanguage = settings.value.activeSubtitleLanguage ?? currentLanguage.value;
  const subtitleFileUrl = props.video.subtitles.find((lang) => lang.languageCode === selectedLanguage)?.value.fileName;

  const vttText = subtitleFileUrl
    ? await fetch(`${signedUrl.value}/${subtitleFileUrl}`)
        .then((res) => res.text())
        .catch(() => '')
    : '';
  const subtitleTimestamps = vttText?.match(/\d{2}:\d{2}:(\d{2}.\d{3})/);
  return subtitleTimestamps ? subtitleTimestamps[1] : ''; // get the start time from the first subtitle timestamp
};

const videoFilePath = computed(() => {
  const { fileId } =
    props.video.videos?.find((video) => video.categoryId === props.category.id && video.areaId === props.area?.id) ??
    {};

  if (!fileId || !props.video.files?.[fileId]) {
    return { hlsPath: '', webmPath: undefined };
  }

  const file = props.video.files[fileId];

  return {
    hlsPath: file.fileName,
    // @ts-ignore TODO fix, publish use new CR type
    webmPath: file.webmFile?.fileName,
  };
});

const getAccessToken: () => Promise<string | undefined> = async () => {
  const tokensStore = useTokens();
  const tokens = await tokensStore.getValidTokens();
  return tokens ? tokens.accessToken : undefined;
};

const initVideoPlayer = async () => {
  if (ILTVideo) {
    ILTVideo.audioHandler.pause();
  }

  if (!signedUrl.value) {
    await getDownloadUrl(props.video.id).then((url) => {
      signedUrl.value = url;
    });
  }

  const startTime = await startTimeFromSubtitle();

  const accessToken = await getAccessToken();

  const showSkipIntroButton = computed(() => {
    const filmId = props.video?.filmId ?? '';
    return /^\d+$/.test(filmId);
  });

  // TODO - remove this after fixing 401 issues
  const hostname = window.location.hostname;
  const hostnameWithoutDots = hostname.replace(/\./g, '');

  ILTVideo = new VideoPlayer({
    userIdentity: {
      type: 'User',
      id: userContext.user.uid,
      roles: userContext.roles.map((role) => role.value),
      organisations: organizations.value.data.map((org: SlimOrganization) => org.uid),
    },
    // Always use 'manabua' here instead of 'manabua'/'begreppa'
    application: 'manabua',
    applicationVersion: appVersion,
    activityStreamEndpoint: `${getApiUrl('activity-stream')}/origin/${hostnameWithoutDots}/events`,
    token: accessToken,
    getAccessToken: getAccessToken,
    siteLanguage: getMarketLanguage(),
    videoUid: props.video.id,
    categoryUid: props.category.id,
    areaUid: props.area?.id,
    hls: `${signedUrl.value}/${videoFilePath.value.hlsPath}`,
    // @ts-ignore TODO fix, publish use new CR type
    webm: videoFilePath.value.webmPath ? `${signedUrl.value}/${videoFilePath.value.webmPath}` : undefined,
    trackName: props.video.title,
    poster: props.video.previewImage,
    playbackRates: [0.5, 0.8, 1, 1.2, 1.5, 2],
    audioTracks: props.video.narrations
      .map(populateLanguage)
      .filter(languageTranslated)
      .map((item) => ({
        langCode: item.languageCode,
        language: item.languageTranslation,
        narratorPath: `${signedUrl.value}/${item.value.fileName}`,
        // @ts-ignore TODO fix, publish use new CR type
        oggNarratorPath: item.value?.ogg?.fileName ? `${signedUrl.value}/${item.value.ogg.fileName}` : undefined,
      })),
    subtitleTracks: props.video.subtitles
      .map(populateLanguage)
      .filter(languageTranslated)
      .map((item) => ({
        langCode: item.languageCode,
        language: item.languageTranslation,
        subtitlePath: `${signedUrl.value}/${item.value.fileName}`,
      })),
    activeAudioTrack: settings.value.activeAudioLanguage ?? currentLanguage.value,
    activeSubtitleTrack: settings.value.activeSubtitleLanguage ?? '',
    audioCaption: t(l.film.sound),
    subtitleCaption: t(l.film.subtitles),
    showSkipIntroBtn: !settings.value.skipVideoIntros && showSkipIntroButton.value,
    skipIntroTime: parseFloat(startTime) || 4.1,
    autoSkipIntro: settings.value.skipVideoIntros && showSkipIntroButton.value,
    skipIntroCaption: t(l.film.skipIntro),
    offCaption: t(l.film.off),
    videoJsConfig: {},
    enabledButtons: ['subtitleSize', 'share', 'fullscreen'],
    route,
    initialVolume: settings.value.videoVolume ? parseFloat(settings.value.videoVolume) : undefined,
    onVideoEnded: () => {
      isVideoEnded.value = true;
    },
    onAudioLanguageChange: (lang: string) => {
      settings.value.activeAudioLanguage = lang;
    },
    onShareBtnClick: () => {
      snackbar.snackbarSuccess(t(l.film.copiedVideoLink));
    },
    onIsPlayingChange: (isPlaying: boolean) => {
      if (isPlaying) {
        stopAllSounds();
        setNarrationPlaying(false);
      }
    },
    onSubtitleLanguageChange: (lang: string) => {
      settings.value.activeSubtitleLanguage = lang;
    },
    onVolumeChange: (volume: number) => {
      settings.value.videoVolume = `${volume}`;
    },
    onVideoEnd: () => {
      showRating.value = true;
    },
    onVideoSlicePlayed: sendVideoSlicePlayedAnalytics,
    i18n: (key: string) => t(key),
  });

  hidden.value = false;
  isVideoEnded.value = false;
  playerInstance.value = ILTVideo;
  videoSessionId.value = ILTVideo.getSessionId();
  setNarrationPlaying(false);
  player.value = ILTVideo.init();
  nextTick(() => {
    ILTVideo?.load();
    focusPlayButton();
  });
};

// Move focus to the play button on initial page entry so keyboard and screen reader users
// land on "Play video" first. Guarded so re-inits (language / skip-intro changes) don't steal focus.
function focusPlayButton() {
  if (hasAutofocusedPlay.value) return;
  const buttons = wrapperEl.value?.querySelectorAll<HTMLElement>(
    '.video-controls__button--play-desktop, .video-controls__button--play-mobile',
  );
  const playButton = Array.from(buttons ?? []).find((button) => button.offsetParent !== null) ?? buttons?.[0];
  if (playButton) {
    playButton.focus();
    hasAutofocusedPlay.value = true;
  }
}

function watchAgain() {
  isVideoEnded.value = false;
  playerInstance.value?.restart();
}

watch(isNarrationAudioPlaying, (val) => {
  if (val) {
    ILTVideo?.pause();
  }
});
watch(currentLanguage, () => {
  initVideoPlayer();
});
watch(
  () => settings.value.skipVideoIntros,
  () => {
    // re-initialize video player if it hasn't started to enable auto skip intro
    if (ILTVideo?.video && ILTVideo.video.currentTime() === 0) {
      initVideoPlayer();
    }
  },
);
watch(
  () => props.video,
  () => {
    initVideoPlayer();
  },
);

onMounted(() => {
  let isInitialisedFromStart = false;
  watchEffect(() => {
    if (!isInitialisedFromStart && organizations.value.data.length > 0) {
      initVideoPlayer();
      isInitialisedFromStart = true;
    }
  });
});

onUnmounted(() => {
  videoSessionId.value = undefined;
  if (ILTVideo) {
    ILTVideo.unload();
  }
});
</script>

<style lang="scss" scoped>
math .fade-enter-active {
  //transition: opacity .2s ease-in;
  -webkit-transition: all 0.35s cubic-bezier(0.86, 0, 0.07, 1);
  -moz-transition: all 0.35s cubic-bezier(0.86, 0, 0.07, 1);
  -o-transition: all 0.35s cubic-bezier(0.86, 0, 0.07, 1);
  transition: all 0.35s cubic-bezier(0.86, 0, 0.07, 1); /* easeInOutQuint */
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.fade-leave,
.fade-leave-active {
  display: none;
}

.player-wrapper {
  position: relative;
}

.videoContainer-inner {
  opacity: 0;
  transition: all 0.3s ease;

  &.active {
    opacity: 1;
  }
}

.watch-again-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 10;
}
</style>
